import React, { useContext } from 'react'
import useAxios from 'hooks/useAxios'
import * as R from 'ramda'
import { Modal } from 'semantic-ui-react'

import { toast } from 'libs/toast'
import { QuestionBuilderContext } from 'organisms/QuestionBuilderCard'
import QuestionConditionModal from '.'
import { Loader } from 'shared/components/Loader'
import { TEMP_ID_PREFIX } from 'organisms/SurveySettingsV2/constants'
import humps from 'humps'
import { QuestionType } from 'packages/question-types'

const FOLLOW_UP_QUESTION_KEYS = [
  'follow_up_comment_question',
  'follow_up_file_upload_question',
  'follow_up_multiple_choice_question',
  'follow_up_multi_select_choice_question',
]

export default function QuestionConditionModalContainer({
  isOpen,
  close,
  questionId,
  surveyToken,
  isPrimary,
}) {
  const { handleFieldChange } = useContext(QuestionBuilderContext)
  const { data: question, loading } = useAxios({
    url: `survey/survey-builder/${surveyToken}/questions/${questionId}/conditional-logics/`,
    key: `${questionId}`,
  })

  const { fetch: postConditionalLogic, loading: isLogicLoading } = useAxios({
    url: `survey/survey-builder/${surveyToken}/questions/${questionId}/conditional-logics/`,
    method: 'post',
    skip: true,
  })

  function submitConditionalLogicForm(settings) {
    const activeConditions = settings.conditions.filter(R.propEq('is_active', true))
    const checkDuplicateValue = (choiceValues) => new Set(choiceValues).size !== choiceValues.length
    const hasDuplicateChoice = R.any((condition) => {
      const shouldCheckChoice =
        condition.action === 'display_follow_up_question' &&
        condition.conditional_question_type === 'follow_up_multiple_choice_question'

      const choices = R.pathOr([], ['follow_up_multiple_choice_question', 'choices'], condition)
      const choiceValues = choices.filter(R.propEq('is_active', true)).map(R.prop('value'))
      return checkDuplicateValue(choiceValues) && shouldCheckChoice
    }, activeConditions)

    const hasEmptyRule = R.any(R.propEq('rules', []), activeConditions)
    if (
      hasEmptyRule &&
      settings.type !== QuestionType.COMMENT &&
      settings.type !== QuestionType.FILE_UPLOAD
    ) {
      toast.error(
        'At least one choice or sentiment needs to be included as a condition before saving.'
      )
    } else if (hasDuplicateChoice) {
      toast.error('Please remove duplicate choices before saving.')
    } else {
      const newConditions = settings.conditions.map((condition) => {
        const payloadCondition = _nullOutUnselectedFollowUpQuestions(condition)

        if (condition.conditional_question_type === 'follow_up_multiple_choice_question') {
          return _overrideMulipleChoiceQuestionChoiceIdBeNull(payloadCondition)
        } else if (
          condition.conditional_question_type === 'follow_up_multi_select_choice_question'
        ) {
          return _overrideMultiSelectQuestionChoiceIdBeNull(payloadCondition)
        } else {
          return payloadCondition
        }
      })

      const data = {
        is_active: settings.is_active,
        conditions: newConditions,
      }

      postConditionalLogic({ data })
        .then((response) => {
          toast.success('Question actions were successfully saved.')
          handleFieldChange('displayFollowUpQuestion', () => response.data.is_active)()
          handleFieldChange('questionCondition', () => humps.camelizeKeys(response.data))()
          close()
        })
        .catch(() => {
          toast.error(
            'Oops! Something went wrong. Please try again or contact Simplesat support for help.'
          )
        })
    }
  }

  function _nullOutUnselectedFollowUpQuestions(condition) {
    const selectedKey = R.includes(condition.conditional_question_type, FOLLOW_UP_QUESTION_KEYS)
      ? condition.conditional_question_type
      : 'follow_up_multiple_choice_question'

    return FOLLOW_UP_QUESTION_KEYS.filter((key) => key !== selectedKey).reduce(
      (payloadCondition, key) => R.assoc(key, null, payloadCondition),
      condition
    )
  }

  function _overrideMulipleChoiceQuestionChoiceIdBeNull(condition) {
    return {
      ...condition,
      follow_up_multiple_choice_question: {
        ...condition.follow_up_multiple_choice_question,
        choices: R.propOr([], 'choices', condition.follow_up_multiple_choice_question).map(
          (choice) => {
            return {
              ...choice,
              id: `${choice.id}`.startsWith(TEMP_ID_PREFIX) ? null : choice.id,
            }
          }
        ),
      },
    }
  }

  function _overrideMultiSelectQuestionChoiceIdBeNull(condition) {
    return {
      ...condition,
      follow_up_multi_select_choice_question: {
        ...condition.follow_up_multi_select_choice_question,
        choices: R.propOr([], 'choices', condition.follow_up_multi_select_choice_question).map(
          (choice) => {
            return {
              ...choice,
              id: `${choice.id}`.startsWith(TEMP_ID_PREFIX) ? null : choice.id,
            }
          }
        ),
      },
    }
  }

  if (loading || !question) {
    return (
      <Modal open={isOpen} size="large" onClose={close}>
        <Modal.Header>Question actions</Modal.Header>
        <Modal.Content>
          <Loader withContainer />
        </Modal.Content>
      </Modal>
    )
  }

  return (
    <QuestionConditionModal
      isLogicLoading={isLogicLoading}
      question={question}
      close={close}
      isOpen={isOpen}
      onSubmit={submitConditionalLogicForm}
      shouldShowChoiceValueOnRule={
        isPrimary || question.type === QuestionType.SLIDER || question.type === QuestionType.STAR
      }
    />
  )
}



// WEBPACK FOOTER //
// ./node_modules/eslint-loader??ref--0-0!./src/organisms/QuestionConditionModal/container.js